import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  IconButton,
  Typography,
} from "@mui/material";
import AppHeader from "../components/AppHeader";
import MainContainer from "../components/MainContainer";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import { IUserDetailsPermissionState } from "../interfaces/store/IUserDetailsPermissionState";
import { hasPermission } from "../utils/permissionUtil";
import { PERMISSION } from "../constants/userPermission";
import { useNavigate, useParams } from "react-router-dom";
import { getDataFromApi } from "../services/api/apiService";
import { RestfulUrls } from "../constants/restfulUrls";
import { IApiResponse } from "../interfaces/common/IApiResponse";
import { useDispatch } from "react-redux";
import { actionTypes } from "../store/actionTypes/actionTypes";
import { RouterPathEnum } from "../constants/router";
import htmlDocx from "html-docx-js/dist/html-docx";
import { saveAs } from "file-saver";

interface ZoomMeetingDetails {
  Id: number;
  MeetingId: string;
  Passcode: string;
  MeetingUrl: string;
}

interface ZoomMeetingResponse {
  status: string;
  data: ZoomMeetingDetails;
}

const Home: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { id } = useParams<{ id: string }>();
  const [zoomMeetingDetails, setZoomMeetingDetails] =
    useState<ZoomMeetingDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [showRetryModal, setShowRetryModal] = useState(false);
  const [countdown, setCountdown] = useState(5);

  const userPermissions = useSelector(
    (state: { userDetailsPermission: IUserDetailsPermissionState }) =>
      state.userDetailsPermission.rolePermission
  );

  const exportLeftLayoutToWord = () => {
    const leftLayout = document.querySelector(".left-layout");
    if (!leftLayout) return;

    // Clone the node to avoid modifying the actual DOM
    const clone = leftLayout.cloneNode(true) as HTMLElement;

    // Remove all elements that are hidden (display: none or type="hidden")
    const hiddenElements = clone.querySelectorAll('[style*="display: none"], [hidden], [type="hidden"]');
    hiddenElements.forEach((el) => el.parentNode?.removeChild(el));

    // Remove button text
    const buttons = clone.querySelectorAll("button, .MuiButton-root");
    buttons.forEach((btn) => btn.textContent = "");

    // Remove specific words from the HTML
    let html = clone.outerHTML;
    html = html
      .replace(/Intervention Summary/gi, "")
      .replace(/Create Agenda Item/gi, "")
      .replace(/Resume Agenda Item/gi, "");

    // Merge consecutive h6 tags into one line with font size 12pt and bold
    html = html.replace(/(<h6[^>]*>.*?<\/h6>)(\s*<h6[^>]*>.*?<\/h6>)+/g, (match) => {
      const texts = Array.from(match.matchAll(/<h6[^>]*>(.*?)<\/h6>/g)).map(m => m[1]);
      return `<span style="font-size:12pt;font-weight:bold;">${texts.join(" ")}</span>`;
    });

    const docx = htmlDocx.asBlob(html);
    saveAs(docx, "Transcripts.docx");
  };

  const fetchZoomMeetingDetails = async () => {
    if (!id) return;

    setLoading(true);
    const url = `${RestfulUrls.BASE_URL}${RestfulUrls.GET_ZOOM_MEETING_DETAILS}${id}`;

    try {
      await getDataFromApi<ZoomMeetingResponse>(
        url,
        (response: IApiResponse<ZoomMeetingResponse>) => {
          if (response.data?.status === "Success" && response.data?.data) {
            const urlSegments = window.location.pathname.split("/");
            const meetingIdFromUrl = urlSegments[urlSegments.length - 1];
            const meetingIdFromApi = response.data.data.MeetingId;

            if (meetingIdFromUrl === meetingIdFromApi) {
              setZoomMeetingDetails(response.data.data);
            }

          }
          setLoading(false);
        },
        { isAuthRequired: true }
      );
    } catch (error) {
      console.error("Error fetching zoom meeting details:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchZoomMeetingDetails();
  }, [id]);

  const handleOpenTranModel = () => {
    //NOTE:: Commented popup open code
    // setIsViewTranModalOpen(true);
    dispatch({
      type: actionTypes.UPDATE_MEETING_ID,
      payload: null,
    });
    dispatch({
      type: actionTypes.UPDATE_FETCH_TRANS_ID,
      payload: zoomMeetingDetails?.MeetingId,
    });
  };


  const handleHeaderTitleClick = async () => {
    navigate(`/${RouterPathEnum.DGO}/${RouterPathEnum.DASHBOARD}`);
    dispatch({
      type: actionTypes.UPDATE_MEETING_ID,
      payload: null,
    });
    dispatch({
      type: actionTypes.UPDATE_FETCH_TRANS_ID,
      payload: null,
    });
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!zoomMeetingDetails) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "20px"
        }}
      >
        Sorry! Meeting not found. The URL may be incorrect or the meeting might not exist.
      </Box>
    );
  }

  return (
    <Box>
      <AppHeader showLogout={true} />
      <Box className="main-body-container">
        <Box className="upper-container">
          <Box className="commitee-container">
            <IconButton className="home-icon" onClick={handleHeaderTitleClick}>
              <HomeOutlinedIcon />
            </IconButton>
            <Typography
              sx={{ cursor: "pointer" }}
              title="Go to dashboard page"
              onClick={handleHeaderTitleClick}
            >
              Dashboard
            </Typography>
            <IconButton className="arrow-icon">
              <ArrowBackIosNewOutlinedIcon />
            </IconButton>
            <Typography>
              {zoomMeetingDetails.MeetingUrl} : {zoomMeetingDetails.MeetingId}
            </Typography>
          </Box>
          <Box>
            <Button
              variant="contained"
              color="primary"
              onClick={exportLeftLayoutToWord}
              className="record-button"
              sx={{ mr: "10px" }}
            >
              Export Transcriptions
            </Button>
            {hasPermission(
              userPermissions,
              PERMISSION?.MODULE?.MEETING_TRANSCRIPTION_PAGE,
              PERMISSION?.ACTION?.VIEW_TRANSCRIPTION
            ) && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleOpenTranModel}
                  className="record-button"
                  sx={{ mr: "10px" }}
                >
                  <span style={{ marginLeft: "5px" }}>View Transcriptions</span>
                </Button>
              )}
          </Box>
        </Box>
        <MainContainer
          setLoading={setLoading}
          showRetryModal={showRetryModal}
          setShowRetryModal={setShowRetryModal}
          countdown={countdown}
          setCountdown={setCountdown}
        />
      </Box>
    </Box>
  );
};

export default Home;
